
import React from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { Phone, Mail, MapPin, Clock } from 'lucide-react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import BookingForm from '@/components/BookingForm';
import SocialLinks from '@/components/SocialLinks';
import { SITE } from '@/data/site';
import { trackPhoneClick } from '@/lib/analytics';

function ContactsPage() {
  return (
    <>
      <Helmet>
        <title>Контакти | {SITE.domain}</title>
        <meta name="description" content="Контакти сервісу автоскла в Києві. Телефони: 097 927 36 54, 099 543 74 38. Адреса: вул. Марка Черемшини, 38а. Запис онлайн." />
      </Helmet>

      <Header />

      <main>
        <section className="py-20 bg-background">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="text-center mb-12"
            >
              <h1 className="text-4xl md:text-5xl font-bold mb-4 text-balance" style={{ letterSpacing: '-0.02em' }}>
                Контакти
              </h1>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto leading-relaxed">
                Зв'яжіться з нами зручним для вас способом або залиште заявку онлайн
              </p>
            </motion.div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-12">
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                className="space-y-8"
              >
                <div className="overflow-hidden rounded-2xl border border-white/10 shadow-lg">
                  <img
                    src={SITE.contactImage}
                    alt="Майстерня Avtosklo Kyiv"
                    className="w-full h-auto max-h-72 object-cover"
                    loading="lazy"
                  />
                </div>
                <div>
                  <h2 className="text-2xl font-semibold mb-6">Контактна інформація</h2>
                  
                  <div className="space-y-6">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <Phone className="w-6 h-6 text-primary" />
                      </div>
                      <div>
                        <h3 className="font-semibold mb-2">Телефони</h3>
                        <div className="space-y-1 text-muted-foreground">
                          <a
                            href={`tel:${SITE.phoneE164Primary}`}
                            className="block hover:text-primary transition-colors text-lg font-semibold"
                            onClick={() => trackPhoneClick(SITE.phoneE164Primary)}
                          >
                            {SITE.phonePrimary}
                          </a>
                          <a
                            href={`tel:${SITE.phoneE164Secondary}`}
                            className="block hover:text-primary transition-colors text-lg font-semibold"
                            onClick={() => trackPhoneClick(SITE.phoneE164Secondary)}
                          >
                            {SITE.phoneSecondary}
                          </a>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <Mail className="w-6 h-6 text-primary" />
                      </div>
                      <div>
                        <h3 className="font-semibold mb-2">Email</h3>
                        <a href={`mailto:${SITE.email}`} className="text-muted-foreground hover:text-primary transition-colors">
                          {SITE.email}
                        </a>
                      </div>
                    </div>

                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <MapPin className="w-6 h-6 text-primary" />
                      </div>
                      <div>
                        <h3 className="font-semibold mb-2">Адреса</h3>
                        <a
                          href={SITE.googleMapsUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-muted-foreground hover:text-primary transition-colors"
                        >
                          {SITE.address}
                        </a>
                      </div>
                    </div>

                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <Clock className="w-6 h-6 text-primary" />
                      </div>
                      <div>
                        <h3 className="font-semibold mb-2">Режим роботи</h3>
                        <div className="text-muted-foreground space-y-1">
                          <p>{SITE.hoursWeekdays}</p>
                          <p>{SITE.hoursSaturday}</p>
                          <p>{SITE.hoursSunday}</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="mt-8">
                    <h3 className="font-semibold mb-4">Соціальні мережі</h3>
                    <SocialLinks />
                  </div>
                </div>
              </motion.div>

              {/* Booking Form */}
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: 0.3 }}
              >
                <div className="bg-card rounded-2xl p-8 shadow-lg">
                  <h2 className="text-2xl font-semibold mb-6">Записатись на прийом</h2>
                  <BookingForm />
                </div>
              </motion.div>
            </div>

            {/* Map */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.4 }}
              className="rounded-2xl overflow-hidden shadow-lg"
            >
              <iframe
                src={SITE.mapsEmbed}
                width="100%"
                height="450"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Карта розташування avtosklo-kiev.com"
              />
              <div className="bg-card px-4 py-3 text-center">
                <a
                  href={SITE.googleMapsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-primary hover:underline"
                >
                  Відкрити в Google Maps
                </a>
              </div>
            </motion.div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}

export default ContactsPage;
